/* eslint-disable react/prop-types */
import { withTheme } from '@emotion/react';
//
import React from 'react';
import { connect } from 'react-redux';
import compose from 'utils/compose';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';

import CheckIcon from '@mui/icons-material/DoneRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import FormikTextField from 'components/Formik/TextField';
import { Field, Form, Formik } from 'formik';

import { accountDiscoveryActions, accountDiscoverySelectors } from 'app-components/flux/account-discovery';
import { institutionLoginsTypes } from 'app-components/flux/institution-logins';

import { getRedirectUrl, tracker } from 'app-components/utils/core';

import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import StdDialogForm from 'components/Dialogs/StdDialogForm';
import StdDialogProgress from 'components/Dialogs/StdDialogProgress';
import Dump from 'components/Dump';
import { subscribeForBroadcastChannel, unsubscribeFromBroadcastChannel } from 'containers/PartnerAuth/broadcastChannel';

import TertiaryTrust from '../TertiaryTrust';

import ErrorContentView from 'components/Dialogs/AccountDiscovery/ErrorContentView';
import ZeroStateView from 'components/ZeroStateView';

import styles from './styles';

const textOverrides = {
  access_denied: 'No accounts were authorized. Go back to your bank and select at least one account to continue.',
};

class InstitutionLoginForm extends React.PureComponent {
  form = null;

  constructor(props) {
    super(props);

    this.state = {
      showPassword: false,

      triggerPartnerAuthSignIn: props.channelData?.partnerAuth && props.hideInitialPartnerAuthIntro,
      partnerAuthInProgress: false,
    };

    tracker.track(tracker.events.fiSignUpScreen, {
      ...props.trackingProperties,
      institution_id: props.institution?.id,
      institution_name: props.institution?.name,
      channel: props.channelData?.type,
      aggregator: props.institution?.aggregator,
    });
  }

  componentDidMount() {
    const { channelData } = this.props;

    // trigger call to getPartnerAuthUris if auth-type is PARTNER_AUTH
    if (channelData?.partnerAuth && !this.props.partnerAuthUris) {
      this.resetPAuth();
    }
  }

  componentDidUpdate(prevProps) {
    const { error, partnerAuthUris, trackingProperties, fixFiMode } = this.props;
    const { partnerAuthInProgress, triggerPartnerAuthSignIn } = this.state;

    if (error && error !== prevProps.error) {
      tracker.track(!fixFiMode ? tracker.events.addFIError : tracker.events.fixFIError, {
        ...trackingProperties,
        error_status: error.status,
        error_code: error.statusCode,
      });
    }
    if (triggerPartnerAuthSignIn && partnerAuthUris && !partnerAuthInProgress) {
      this.doPartnerAuthSignIn();

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ triggerPartnerAuthSignIn: false });
    }
  }

  resetPAuth = (partnerAuthError) => {
    const { channelData, scope } = this.props;
    this.props.getPartnerAuthUris(
      { partnerAuth: channelData.partnerAuth, redirectUri: `${getRedirectUrl()}/partner-auth?scope=${scope}` },
      { scope },
    );
    this.setState({ partnerAuthInProgress: false, partnerAuthError });
  };

  unsubscribeFromBroadcastChannel = () => {
    unsubscribeFromBroadcastChannel(this.props.scope, this.broadcastChannelListener);
    this.broadcastChannelListener = undefined;
  };

  doPartnerAuthSignIn = () => {
    const { channelData, partnerAuthUris, scope } = this.props;
    const partnerAuthUri = partnerAuthUris.get(0);

    const width = 600;
    const height = 650;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    const features = `width=${width},height=${height},top=${top},left=${left}`;
    const child = window.open(partnerAuthUri, '', features);
    child?.focus();
    this.setState({ partnerAuthInProgress: true, partnerAuthError: null });

    this.broadcastChannelListener = (event) => {
      let partnerAuthError;
      const { responseToken } = event.data;
      if (responseToken) {
        const credentials = channelData.partnerAuth.fields?.map((field) => ({
          key: field.key,
          value: field.displayLabel === 'response_token' ? responseToken : '',
        }));
        this.doSubmit(channelData, credentials);
      } else {
        const { errorDescription, errorCode } = event.data;
        partnerAuthError = {
          statusCode: errorCode,
          text: textOverrides[errorCode] ?? ((errorDescription && atob(errorDescription)) || 'Unexpected error'),
        };
      }
      this.resetPAuth(partnerAuthError);
      this.unsubscribeFromBroadcastChannel();
    };
    subscribeForBroadcastChannel(scope, this.broadcastChannelListener);
  };

  handleCancel = () => {
    const { onCancel } = this.props;

    if (onCancel) {
      onCancel({
        trackingProperties: {
          step: 'credentials',
        },
      });
    }
  };

  handleClickShowPassword = () => this.setState((prevState) => ({ showPassword: !prevState.showPassword }));

  handlePartnerAuthSignIn = () => {
    this.setState({ triggerPartnerAuthSignIn: true });

    tracker.track(tracker.events.fiSignUpSignUp, {
      ...this.props.trackingProperties,
      institution_id: this.props.institution?.id,
      institution_name: this.props.institution?.name,
      channel: this.props.channelData?.type,
      aggregator: this.props.institution?.aggregator,
    });
  };

  handleSubmit = (values, _actions) => {
    const { channelData } = this.props;
    // Exclude fields with type 'COOKIE' as discussed with QCS team.
    // These fields cannot be excluded at the transformers level
    // as credentials encryption fails if all fields are not considered.
    const credentials = channelData.formFields
      .filter((field) => field?.type !== 'COOKIE')
      .map((field) => ({
        key: field.key,
        value: values[field.key],
      }));
    this.doSubmit(channelData, credentials);
  };

  doSubmit = (channelData, credentials) => {
    tracker.track(tracker.events.fiSignUpSignUp, {
      ...this.props.trackingProperties,
      institution_id: this.props.institution?.id,
      institution_name: this.props.institution?.name,
      channel: this.props.channelData?.type,
      aggregator: this.props.institution?.aggregator,
    });

    if (this.props.onSubmit) {
      const institutionLogin =
        this.props.institutionLogin ||
        institutionLoginsTypes.mkInstitutionLoginClient({
          institutionId: this.props.institution.id,
          channel: channelData.type,

          name: this.props.institution.name,
        });
      this.props.onSubmit(
        institutionLogin.set('credentials', credentials),
        () => {
          this.form?.setSubmitting?.(false);
        },
        () => {
          this.form?.setSubmitting?.(false);
        },
      );
    }
  };

  renderField = (field, index, formikBag, classes, theme) => {
    const isPasswordField = field.type === 'PASSWORD';
    const isCookie = field.type === 'COOKIE';
    // Exclude fields with type 'COOKIE' as discussed with QCS team.
    // These fields cannot be excluded at the transformers level
    // as credentials encryption fails if all fields are not considered.
    if (!isCookie) {
      return (
        <Box display='flex' key={field.key} pt={1}>
          <Field
            component={FormikTextField}
            fullWidth
            label={field.displayLabel}
            name={field.key}
            placeholder='answer'
            variant='filled'
            type={!isPasswordField || this.state.showPassword ? 'text' : 'password'}
            autoComplete={isPasswordField ? 'new-password' : null}
            helperText=' '
            InputProps={{
              id: `field#${index}`,
              autoFocus: index === 0,
              endAdornment: isPasswordField ? (
                <InputAdornment onClick={this.handleClickShowPassword} position='end'>
                  {this.state.showPassword ? (
                    <VisibilityOffIcon style={{ color: theme.palette.icon.light, cursor: 'pointer' }} />
                  ) : (
                    <VisibilityIcon style={{ color: theme.palette.icon.light, cursor: 'pointer' }} />
                  )}
                </InputAdornment>
              ) : null,
              inputProps: {
                maxLength: 214, // max that RSA 2048 encryption could do
              },
            }}
          />
        </Box>
      );
    }
    return null;
  };

  renderSecurityMessage = (icon, title, message) => (
    <Box display='flex' flexDirection='column' width='220px'>
      <Box display='flex'>
        <Box component={icon} color='text.secondary' mt='2px' width='18px' />
        <Box display='flex' flexDirection='column' ml={1} mb={3}>
          <Typography color='textPrimary' variant='bodyRegular'>
            {title}
          </Typography>
          <Typography color='textSecondary' variant='paragraphRegular'>
            {message}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  renderCredentialsForm = () => {
    const {
      classes,
      error,
      featureFlags,
      channelData,
      fiBrandingIndent,
      fullAddAccountsDialog,
      theme,
      trackingProperties,
    } = this.props;

    const showSecurityMessages = fullAddAccountsDialog && featureFlags.get('addfiSecPrivTrust');
    if (showSecurityMessages) {
      tracker.track(tracker.events.viewSecurityMsg, { ...trackingProperties });
    }

    const cancelButtonLabel = fullAddAccountsDialog ? null : 'Cancel';
    const submitButtonLabel = fullAddAccountsDialog ? 'Connect' : 'Submit';

    return (
      <Formik
        className={classes.formik}
        initialValues={{}}
        onSubmit={this.handleSubmit}
        validate={(values) => {
          // Exclude fields with type 'COOKIE' as discussed with QCS team.
          // These fields cannot be excluded at the transformers level
          // as credentials encryption fails if all fields are not considered.
          const validateResult = channelData.formFields
            .filter((field) => field?.type !== 'COOKIE')
            .reduce(
              (result, field) => ({
                ...result,
                ...(!values[field.key] ? { [field.key]: 'Required' } : null),
              }),
              {},
            );
          return validateResult;
        }}
      >
        {(formikBag) => {
          const { handleSubmit: formikHandleSubmit, isSubmitting } = formikBag;
          this.form = formikBag; // required to access properties within Formik
          return (
            <StdDialogForm component={Form} onSubmit={formikHandleSubmit}>
              <StdDialogContent
                id='institution_login_credentials_form_dialog'
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  pl: `${fiBrandingIndent}px`,
                }}
              >
                <Dump obj={this.props.institution} />
                {channelData?.formFields?.size ? (
                  <Box display='flex' flex='1' flexDirection='column' id='hello'>
                    {error?.text && <ErrorContentView error={error} />}
                    {channelData.formFields.map((field, index) =>
                      this.renderField(field, index + 4, formikBag, classes, theme),
                    )}
                  </Box>
                ) : (
                  <Box flex='1'>This Bank is not available right now</Box>
                )}
                {showSecurityMessages && (
                  <>
                    <Divider flexItem orientation='vertical' sx={{ ml: theme.spacing(2), mr: theme.spacing(2) }} />
                    <Box display='flex' flex='0 0 220px' flexDirection='column'>
                      {this.renderSecurityMessage(
                        LockOutlinedIcon,
                        'Security',
                        'Your information is encrypted end-to-end.',
                      )}
                      {this.renderSecurityMessage(
                        SecurityIcon,
                        'Privacy',
                        'Control which accounts you give Quicken Simplifi access to.',
                      )}
                      {this.renderSecurityMessage(
                        ThumbUpIcon,
                        'Trust',
                        'For 30 years, Quicken has continued to be trusted by millions of consumers.',
                      )}
                    </Box>
                  </>
                )}
              </StdDialogContent>
              <StdDialogActions
                primary={{
                  children: submitButtonLabel,
                  disabled: !channelData || isSubmitting,
                  id: 'connectButton',
                }}
                secondary={{
                  children: cancelButtonLabel,
                  onClick: !fullAddAccountsDialog && this.handleCancel,
                  variant: 'outlined',
                }}
                tertiaryNode={fullAddAccountsDialog && !showSecurityMessages && <TertiaryTrust />}
              />
            </StdDialogForm>
          );
        }}
      </Formik>
    );
  };

  renderPartnerAuth = () => {
    const { classes, error, partnerAuthUris, theme } = this.props;
    const { partnerAuthError, partnerAuthInProgress } = this.state;
    const errorToDisplay = error || partnerAuthError;
    const isLoadingPartnerAuthInfo = !partnerAuthUris && !errorToDisplay;

    return (
      <>
        <StdDialogContent id='partner_auth_dialog'>
          <Dump obj={this.props.institution} />
          {!partnerAuthUris && error && <Box>This Bank is not available right now</Box>}
          {isLoadingPartnerAuthInfo && <StdDialogProgress icon={CheckIcon} label='Securing connection...' />}
          <Box style={{ visibility: isLoadingPartnerAuthInfo ? 'hidden' : null }}>
            <Typography variant='titleRegular'>Authorization access required</Typography>
            {errorToDisplay && <ErrorContentView error={errorToDisplay} />}
            <Box component='ul' color={theme.palette.text.primary} pl='30px'>
              <li>
                <Typography variant='paragraphRegular' className={classes.partnerListItem}>
                  Sign in to {this.props.institution?.name || 'your bank'} through the secure browsing window.
                </Typography>
              </li>
              <li>
                <Typography variant='paragraphRegular' className={classes.partnerListItem}>
                  When prompted, SELECT ALL YOUR ACCOUNTS.
                </Typography>
              </li>
              <li>
                <Typography variant='paragraphRegular' className={classes.partnerListItem}>
                  {"You'll be able to hide accounts in Quicken after a secure connection is established."}
                </Typography>
              </li>
            </Box>
          </Box>
          {partnerAuthInProgress && (
            <ZeroStateView
              in={partnerAuthInProgress}
              center
              loading
              button='Cancel'
              onClick={() => {
                this.resetPAuth();
                this.unsubscribeFromBroadcastChannel();
              }}
              sx={{
                background: theme.palette.backdrop.light,
                width: 220,
                height: 220,
                borderRadius: 12,
                backdropFilter: 'blur(4px)',
                border: `1px solid ${theme.palette.border.light}`,
              }}
            />
          )}
        </StdDialogContent>
        {partnerAuthUris && (
          <StdDialogActions
            primary={{
              children: !partnerAuthInProgress ? 'Sign In' : <CircularProgress size={24} />,
              disabled: isLoadingPartnerAuthInfo || partnerAuthInProgress,
              id: 'connectButton',
              onClick: this.handlePartnerAuthSignIn,
            }}
          />
        )}
      </>
    );
  };

  render() {
    return this.props.channelData?.authType === 'PARTNER_AUTH'
      ? this.renderPartnerAuth()
      : this.renderCredentialsForm();
  }
}

function mapStateToProps(state, props) {
  const { scope } = props;
  return {
    channelData: accountDiscoverySelectors.getInstitutionChannelData(state, { scope }),
    partnerAuthUris: accountDiscoverySelectors.getPartnerAuthUris(state, { scope }),
    featureFlags: featureFlagsSelectors.getFeatureFlags(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    getPartnerAuthUris: (payload, meta) => dispatch(accountDiscoveryActions.getPartnerAuthUris(payload, meta)),
  };
}

export default compose(
  withTheme,
  connect(mapStateToProps, mapDispatchToProps),
)(withStyles(InstitutionLoginForm, styles));
